/* Auto-generated from the renderer theme.css — injected into the preview iframe so the real app styling is isolated from platform-react. */
export const A2_THEME = `/*
 * BeX 3D Apps — renderer styles.
 *
 * All visuals are driven by CSS custom properties at :root. Themes from BeXO
 * override these via document.documentElement.style.setProperty(). Defaults
 * here mirror BeXO's default theme (app/appengine/data/default_theme.json) —
 * a polished modern light theme — so unthemed and default-themed apps look
 * identical.
 */

:root {
  /* Palette */
  --bg:            #f7f8fa;
  --bg-elevated:   #ffffff;
  --surface:       #ffffff;
  --surface-muted: #f1f3f6;
  --border:        #e3e6eb;
  --border-strong: #cfd4dc;
  --text:          #16191f;
  --text-muted:    #667085;
  --primary:       #4f46e5;
  --primary-text:  #ffffff;
  --primary-hover: #4338ca;
  --accent:        #6366f1;

  --success: #16a34a;
  --warning: #b45309;
  --error:   #dc2626;
  --info:    #0e7490;
  --neutral: #667085;

  /* Geometry & rhythm */
  --radius:    12px;
  --radius-sm: 8px;
  --radius-lg: 16px;
  --gap:       16px;
  --gap-sm:    8px;
  --gap-lg:    24px;

  /* Elevation */
  --shadow:    0 1px 2px rgba(16,24,40,0.05), 0 1px 3px rgba(16,24,40,0.06);
  --shadow-md: 0 4px 12px -2px rgba(16,24,40,0.10), 0 2px 6px -2px rgba(16,24,40,0.06);
  --shadow-lg: 0 16px 40px -8px rgba(16,24,40,0.16);

  /* Typography scale */
  --font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  --font-mono:   ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  --fs-xs:  12px;
  --fs-sm:  13px;
  --fs-md:  14px;
  --fs-lg:  16px;
  --fs-xl:  20px;
  --fs-2xl: 28px;
  --max-width: 1100px;

  /* Motion */
  --transition: 160ms cubic-bezier(0.2, 0, 0, 1);
}

* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  font-family: var(--font-family);
  font-size: var(--fs-md);
  color: var(--text);
  background: var(--bg);
  line-height: 1.55;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app { min-height: 100vh; }
.a2-root { max-width: var(--max-width); margin: 0 auto; padding: 36px 24px 72px; }
.a2-loading, .a2-fatal {
  max-width: var(--max-width); margin: 0 auto; padding: 64px 20px;
  color: var(--text-muted); text-align: center; font-size: var(--fs-md);
}
.a2-fatal h2 { color: var(--error); margin-top: 0; }

/* ── Loading feedback ────────────────────────────────────────────────────
   Backend round-trips (journey open / action / sign-out) can take a moment
   (Mongo-bound). Give the user clear feedback so the screen never looks
   frozen: a thin top progress bar on every in-flight request, plus a gentle
   dim+lock of the surface (also prevents double-submits), and a spinner on
   the initial boot. */
.a2-loading { display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 14px; min-height: 50vh; }
.a2-spinner {
  width: 34px; height: 34px; border-radius: 50%;
  border: 3px solid var(--border); border-top-color: var(--primary);
  animation: a2-spin 0.8s linear infinite;
}
@keyframes a2-spin { to { transform: rotate(360deg); } }

/* Thin indeterminate progress bar, fixed to the very top of the viewport. */
.a2-loadbar {
  position: fixed; top: 0; left: 0; right: 0; height: 3px;
  z-index: 9999; pointer-events: none; overflow: hidden;
  opacity: 0; transition: opacity 0.2s ease;
}
.a2-loadbar.active { opacity: 1; }
.a2-loadbar.active::before {
  content: ""; position: absolute; top: 0; height: 100%; width: 35%;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(90deg,
    transparent, var(--primary) 40%, var(--primary) 60%, transparent);
  animation: a2-loadbar-slide 1.1s ease-in-out infinite;
}
@keyframes a2-loadbar-slide {
  0%   { left: -35%; }
  100% { left: 100%; }
}

/* While a request is outstanding, dim + lock the current surface — signals
   "working" and prevents a second click landing on a stale screen. */
.a2-root.is-loading {
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
@media (prefers-reduced-motion: reduce) {
  .a2-spinner, .a2-loadbar.active::before { animation-duration: 0s; }
}
.a2-placeholder {
  background: color-mix(in srgb, var(--warning) 10%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--warning) 40%, transparent);
  border-radius: var(--radius-sm); padding: 8px 12px;
  font-size: var(--fs-sm);
  color: color-mix(in srgb, var(--warning) 80%, var(--text));
}

/* ========== Layout primitives ========== */

.a2-col { display: flex; flex-direction: column; gap: var(--gap); }
.a2-row {
  display: flex; flex-direction: row; gap: var(--gap);
  align-items: stretch;
}
.a2-row.align-center      { align-items: center; }
.a2-col.align-center      { align-items: center; }
.a2-col.align-start,  .a2-row.align-start       { align-items: flex-start; }
.a2-col.align-end,    .a2-row.align-end         { align-items: flex-end; }
.a2-col.align-stretch                            { align-items: stretch; }
.a2-row.align-spaceBetween                       { justify-content: space-between; align-items: center; }

/* When two cards are siblings inside a Row, share width equally. */
.a2-row > .a2-card { flex: 1; min-width: 0; }

.a2-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 22px 24px;
  transition: box-shadow var(--transition), transform var(--transition),
              border-color var(--transition);
}

.a2-list { display: flex; flex-direction: column; gap: 10px; }
.a2-list.dir-horizontal { flex-direction: row; flex-wrap: wrap; }
/* List row hover — template rows that are Cards lift gently. */
.a2-list > .a2-card:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--border-strong);
  transform: translateY(-1px);
}
.a2-list-empty {
  color: var(--text-muted); font-size: var(--fs-md); padding: 18px;
  background: var(--surface-muted); border-radius: var(--radius-sm);
  border: 1px dashed var(--border);
  text-align: center;
}

/* ── Demo App home: tour-stop launcher grid ──────────────────────────────
   A horizontal List is used only by the home launcher, so styling it as a
   responsive card grid is safe. Each card is clickable (cursor + a stronger
   lift than the default list-row hover), and the leading Icon becomes a
   tinted badge — a modern launcher instead of a flat text-button list. */
.a2-list.dir-horizontal {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--gap);
}
.a2-list.dir-horizontal > .a2-card {
  cursor: pointer;
  height: 100%;
  padding: 18px 20px;
}
.a2-list.dir-horizontal > .a2-card:hover {
  transform: translateY(-3px);
}

.a2-divider { background: var(--border); }
.a2-divider.axis-horizontal { height: 1px; width: 100%; }
.a2-divider.axis-vertical   { width: 1px; align-self: stretch; }

.a2-tabs { display: flex; flex-direction: column; gap: 16px; }
.a2-tabs-bar { display: flex; gap: 4px; border-bottom: 1px solid var(--border); }
.a2-tab {
  padding: 10px 14px; background: transparent; border: none;
  border-bottom: 2px solid transparent; cursor: pointer;
  font: inherit; font-weight: 500; color: var(--text-muted);
  transition: color var(--transition), border-color var(--transition),
              background-color var(--transition);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}
.a2-tab.active { color: var(--primary); border-bottom-color: var(--primary); }
.a2-tab:hover:not(.active) { color: var(--text); background: var(--surface-muted); }
.a2-tab:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--primary) 55%, transparent);
  outline-offset: -2px;
}

/* ========== Modal ========== */

.a2-modal-trigger { cursor: pointer; }
.a2-modal-backdrop {
  position: fixed; inset: 0; background: rgba(10,13,18,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 100; backdrop-filter: blur(3px);
  animation: a2-fade-in 160ms ease-out;
}
.a2-modal-panel {
  position: relative; background: var(--surface); padding: 28px;
  border-radius: var(--radius-lg); box-shadow: var(--shadow-lg);
  max-width: 520px; width: calc(100% - 32px);
  max-height: 82vh; overflow-y: auto;
  animation: a2-pop-in 200ms cubic-bezier(0.34, 1.4, 0.64, 1);
}
.a2-modal-close {
  position: absolute; top: 14px; right: 14px;
  background: transparent; border: none; cursor: pointer;
  font-size: 24px; color: var(--text-muted); line-height: 1;
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  transition: background-color var(--transition), color var(--transition);
}
.a2-modal-close:hover { color: var(--text); background: var(--surface-muted); }
@keyframes a2-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes a2-pop-in  {
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
}

/* ========== Display ========== */

.a2-text { margin: 0; min-width: 0; }
.a2-text.use-heading {
  font-size: var(--fs-2xl); font-weight: 700; letter-spacing: -0.025em;
  line-height: 1.25; color: var(--text);
}
.a2-text.use-subheading {
  font-size: var(--fs-lg); font-weight: 600; color: var(--text);
  letter-spacing: -0.01em; line-height: 1.35;
}
.a2-text.use-body    { font-size: var(--fs-md); }
.a2-text.use-caption { font-size: var(--fs-sm); color: var(--text-muted); }
.a2-text.use-error   { font-size: var(--fs-sm); color: var(--error); min-height: 18px; font-weight: 500; }

.a2-image                 { display: block; max-width: 100%; border-radius: var(--radius-sm); }
.a2-image.fit-cover       { object-fit: cover; }
.a2-image.fit-contain     { object-fit: contain; }
.a2-image.fit-fill        { object-fit: fill; }
.a2-image.fit-scaleDown   { object-fit: scale-down; }

/* Don't let a flex column stretch an image to the column's cross-axis
 * width — without this, intrinsic-sized images (e.g. logos) get blown
 * up to full container width because the default align-items on a flex
 * column is \`stretch\`. The image keeps its natural size, capped by any
 * surface-supplied maxWidth on the Image component itself. */
.a2-col > .a2-image       { align-self: flex-start; }

.a2-icon  { display: inline-block; line-height: 1; font-size: 18px; }
.a2-video { width: 100%; border-radius: var(--radius-sm); }
.a2-audio { display: flex; flex-direction: column; gap: 4px; }
.a2-audio audio { width: 100%; }
.a2-audio-desc  { font-size: var(--fs-sm); color: var(--text-muted); }

/* ========== Nested values (the "no raw JSON" rule) ========== */

.a2-empty { color: var(--text-muted); }

.a2-chips { display: inline-flex; flex-wrap: wrap; gap: 6px; vertical-align: middle; }
.a2-chip {
  display: inline-flex; align-items: center;
  padding: 2px 10px; border-radius: 999px;
  background: var(--surface-muted); border: 1px solid var(--border);
  font-size: var(--fs-xs); font-weight: 500; color: var(--text);
  white-space: nowrap; max-width: 180px;
  overflow: hidden; text-overflow: ellipsis;
}
.a2-chip-more { color: var(--text-muted); background: transparent; border-style: dashed; }

.a2-kv {
  display: grid; grid-template-columns: minmax(72px, max-content) 1fr;
  column-gap: 14px; row-gap: 4px;
  font-size: var(--fs-sm); text-align: left;
}
.a2-kv-row { display: contents; }
.a2-kv-key {
  color: var(--text-muted); font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 180px;
}
.a2-kv-key.a2-kv-more { font-weight: 400; font-style: italic; }
.a2-kv-val { min-width: 0; overflow-wrap: anywhere; }
/* Nested kv blocks indent gently instead of bracing. */
.a2-kv .a2-kv {
  border-left: 2px solid var(--border);
  padding-left: 10px; margin: 2px 0;
}

.a2-collection { display: inline-flex; align-items: center; gap: 8px; }
.a2-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 22px; padding: 0 7px; border-radius: 999px;
  background: color-mix(in srgb, var(--primary) 12%, transparent);
  color: var(--primary); font-size: var(--fs-xs); font-weight: 600;
}
.a2-collection-summary { color: var(--text-muted); font-size: var(--fs-sm); }
.a2-obj-summary { color: var(--text); }

/* ========== Inputs ========== */

.a2-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.a2-field-label {
  font-size: var(--fs-sm); color: var(--text); font-weight: 500;
  letter-spacing: 0.01em;
}
.a2-field-input {
  width: 100%; padding: 10px 14px;
  font: inherit; color: inherit;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  transition: border-color var(--transition), box-shadow var(--transition),
              background-color var(--transition);
}
.a2-field-input::placeholder { color: color-mix(in srgb, var(--text-muted) 75%, transparent); }
.a2-field-input:hover:not(:focus):not(:disabled) { border-color: var(--border-strong); }
.a2-field-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 16%, transparent);
}
.a2-field-input:disabled {
  background: var(--surface-muted); color: var(--text-muted);
  cursor: not-allowed;
}
textarea.a2-field-input { min-height: 96px; resize: vertical; line-height: 1.5; }

.a2-checkbox {
  display: inline-flex; gap: 10px; align-items: center; cursor: pointer;
  font-size: var(--fs-md); padding: 4px 0;
}
.a2-checkbox input[type="checkbox"] {
  accent-color: var(--primary); width: 17px; height: 17px;
  cursor: pointer; margin: 0;
}
.a2-checkbox:hover { color: color-mix(in srgb, var(--primary) 60%, var(--text)); }

.a2-multichoice { display: flex; flex-direction: column; gap: 4px; }
.a2-multichoice.chips { flex-direction: row; flex-wrap: wrap; gap: 8px; }
.a2-multichoice.chips .a2-multichoice-item button {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 999px; padding: 7px 16px; font: inherit; font-weight: 500;
  cursor: pointer; color: var(--text);
  transition: background-color var(--transition), border-color var(--transition),
              box-shadow var(--transition), transform 80ms;
}
.a2-multichoice.chips .a2-multichoice-item button:hover {
  border-color: color-mix(in srgb, var(--primary) 45%, var(--border));
  box-shadow: var(--shadow);
  transform: translateY(-1px);
}
.a2-multichoice.chips .a2-multichoice-item.selected button {
  background: var(--primary); color: var(--primary-text); border-color: var(--primary);
  box-shadow: 0 1px 3px color-mix(in srgb, var(--primary) 35%, transparent);
}
.a2-multichoice-item {
  display: inline-flex; gap: 10px; align-items: center; font-size: var(--fs-md);
  padding: 7px 10px; border-radius: var(--radius-sm); cursor: pointer;
  transition: background-color var(--transition);
}
.a2-multichoice:not(.chips) .a2-multichoice-item:hover { background: var(--surface-muted); }
.a2-multichoice-item input {
  accent-color: var(--primary); width: 16px; height: 16px;
  cursor: pointer; margin: 0;
}

.a2-slider { display: flex; flex-direction: column; gap: 8px; }
.a2-slider input[type="range"] { width: 100%; accent-color: var(--primary); cursor: pointer; }

/* ========== Button ========== */

.a2-button {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 10px 18px; font: inherit; font-weight: 500; font-size: var(--fs-md);
  background: var(--surface); color: var(--text);
  border: 1px solid var(--border); border-radius: var(--radius-sm);
  cursor: pointer; box-shadow: 0 1px 2px rgba(16,24,40,0.04);
  transition: background-color var(--transition), border-color var(--transition),
              box-shadow var(--transition), transform 80ms;
  white-space: nowrap;
}
.a2-button:hover:not([disabled]) {
  border-color: var(--border-strong);
  background: var(--surface-muted);
  box-shadow: var(--shadow);
}
.a2-button:active:not([disabled]) { transform: translateY(1px); box-shadow: none; }
.a2-button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
}
.a2-button.primary {
  background: var(--primary); color: var(--primary-text); border-color: var(--primary);
  box-shadow: 0 1px 2px color-mix(in srgb, var(--primary) 35%, transparent);
}
.a2-button.primary:hover:not([disabled]) {
  background: var(--primary-hover); border-color: var(--primary-hover);
  box-shadow: 0 2px 6px color-mix(in srgb, var(--primary) 40%, transparent);
}
.a2-button[disabled] { opacity: 0.55; cursor: progress; }
.a2-button-sm { padding: 5px 12px; font-size: var(--fs-sm); }
/* Buttons inside Buttons (label is a Text component returning a <p>) need
   to lose the inner <p> default margin. */
.a2-button .a2-text { margin: 0; }

/* ========== BeX custom ========== */

.a2-turnstile-placeholder {
  padding: 12px 16px; background: var(--surface-muted); border: 1px dashed var(--border);
  border-radius: var(--radius-sm); color: var(--text-muted);
  font-size: var(--fs-xs); text-align: center;
}

.a2-datatable {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); overflow: hidden;
  box-shadow: var(--shadow);
}
.a2-datatable-scroll { overflow: auto; max-height: 560px; scrollbar-width: thin; }
.a2-datatable table { width: 100%; border-collapse: collapse; font-size: var(--fs-md); }
.a2-datatable th, .a2-datatable td {
  padding: 12px 16px; text-align: left;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}
.a2-datatable th {
  position: sticky; top: 0; z-index: 2;
  background: var(--surface-muted); font-weight: 600;
  color: var(--text-muted); font-size: var(--fs-xs);
  text-transform: uppercase; letter-spacing: 0.05em;
  border-bottom: none;
  box-shadow: inset 0 -1px 0 var(--border);
  user-select: none; white-space: nowrap;
}
.a2-datatable th.sortable { cursor: pointer; transition: color var(--transition); }
.a2-datatable th.sortable:hover { color: var(--text); }
.a2-sort-arrow { margin-left: 5px; font-size: 9px; color: var(--primary); }
.a2-datatable tbody tr { transition: background-color var(--transition); }
.a2-datatable tbody tr:nth-child(even) {
  background: color-mix(in srgb, var(--surface-muted) 45%, var(--surface));
}
.a2-datatable tbody tr:hover {
  background: color-mix(in srgb, var(--primary) 5%, var(--surface));
}
.a2-datatable tbody tr:last-child td { border-bottom: none; }
.a2-cell-trunc {
  display: inline-block; max-width: 280px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  vertical-align: bottom;
}
.a2-datatable td.action-col, .a2-datatable th.action-col {
  width: 1%; white-space: nowrap; text-align: right;
}
.a2-datatable-empty {
  color: var(--text-muted); text-align: center; padding: 36px 16px !important;
}

/* Skeletons — shown while a screen's data loads after an optimistic (instant)
   render. A smooth, subtle shimmer; generic (used by any data component), not a
   spinner. Honors reduced-motion. */
@keyframes a2-shimmer { 100% { background-position-x: -200%; } }
.a2-skeleton-bar, .a2-skeleton-card {
  display: block; border-radius: var(--radius-sm);
  background: linear-gradient(90deg,
    var(--surface-muted) 0%,
    color-mix(in srgb, var(--border) 65%, var(--surface-muted)) 50%,
    var(--surface-muted) 100%);
  background-size: 200% 100%;
  animation: a2-shimmer 1.3s ease-in-out infinite;
}
.a2-skeleton-bar { height: 0.85em; width: 70%; }
.a2-skeleton-row td { padding: 14px 16px; }
.a2-skeleton-row td:nth-child(odd) .a2-skeleton-bar { width: 55%; }
.a2-skeleton-row td:nth-child(3n) .a2-skeleton-bar { width: 80%; }
.a2-skeleton-card { height: 92px; margin-bottom: 12px; }
@media (prefers-reduced-motion: reduce) {
  .a2-skeleton-bar, .a2-skeleton-card { animation-duration: 0s; }
}
.a2-datatable-pager {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: var(--surface-muted); border-top: 1px solid var(--border);
}
.a2-datatable-pager button {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm);
  padding: 5px 12px; cursor: pointer; font: inherit; color: var(--text);
  transition: background-color var(--transition), border-color var(--transition);
}
.a2-datatable-pager button:not([disabled]):hover {
  background: var(--bg-elevated); border-color: var(--border-strong);
}
.a2-datatable-pager button[disabled] { opacity: 0.45; cursor: default; }
.a2-datatable-pager span { margin-left: auto; font-size: var(--fs-sm); color: var(--text-muted); }

.a2-form { display: flex; flex-direction: column; gap: var(--gap); }

.a2-stepper { display: flex; gap: 0; align-items: stretch; padding: 8px 0; }
.a2-step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; position: relative; }
.a2-step:not(:last-child)::after {
  content: ""; position: absolute; top: 16px; left: 60%; right: -40%;
  height: 2px; background: var(--border);
  transition: background-color var(--transition);
}
.a2-step.done:not(:last-child)::after { background: var(--success); }
.a2-step-circle {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--surface); border: 2px solid var(--border);
  font-weight: 600; font-size: var(--fs-sm);
  position: relative; z-index: 1;
  transition: all var(--transition);
}
.a2-step.current .a2-step-circle {
  border-color: var(--primary); color: var(--primary);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--primary) 12%, transparent);
}
.a2-step.done    .a2-step-circle { border-color: var(--success); background: var(--success); color: #fff; }
.a2-step-label   { font-size: var(--fs-xs); color: var(--text-muted); text-align: center; }
.a2-step.current .a2-step-label  { color: var(--text); font-weight: 500; }

.a2-progress-linear { display: flex; flex-direction: column; gap: 6px; }
.a2-progress-track  { height: 8px; background: var(--surface-muted); border-radius: 4px; overflow: hidden; }
.a2-progress-fill   {
  height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, var(--primary), var(--accent));
  transition: width 240ms ease;
}
.a2-progress-label  { font-size: var(--fs-sm); color: var(--text-muted); }
.a2-progress-circular { display: inline-flex; align-items: center; gap: 8px; }
.a2-progress-circular svg .track { fill: none; stroke: var(--surface-muted); stroke-width: 4; }
.a2-progress-circular svg .fill  {
  fill: none; stroke: var(--primary); stroke-width: 4;
  stroke-linecap: round; transition: stroke-dashoffset 240ms ease;
}

.a2-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px; border-radius: 999px;
  font-size: var(--fs-xs); font-weight: 600; letter-spacing: 0.01em;
  line-height: 1.6; white-space: nowrap;
}
.a2-badge::before {
  content: ""; width: 6px; height: 6px; border-radius: 50%;
  background: currentColor; flex-shrink: 0;
}
.a2-badge.variant-success  { background: color-mix(in srgb, var(--success) 13%, transparent); color: color-mix(in srgb, var(--success) 85%, var(--text)); }
.a2-badge.variant-warning  { background: color-mix(in srgb, var(--warning) 13%, transparent); color: color-mix(in srgb, var(--warning) 85%, var(--text)); }
.a2-badge.variant-error    { background: color-mix(in srgb, var(--error) 12%, transparent);   color: color-mix(in srgb, var(--error) 85%, var(--text)); }
.a2-badge.variant-info     { background: color-mix(in srgb, var(--info) 12%, transparent);    color: color-mix(in srgb, var(--info) 85%, var(--text)); }
.a2-badge.variant-neutral  { background: var(--surface-muted); color: var(--text-muted); }
/* A pill is content-sized — never stretched by its flex parent. */
.a2-col > .a2-badge { align-self: flex-start; }
.a2-row > .a2-badge { align-self: center; }

.a2-avatar {
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%; background: color-mix(in srgb, var(--primary) 12%, var(--surface-muted));
  overflow: hidden; flex-shrink: 0; font-weight: 600;
  color: color-mix(in srgb, var(--primary) 75%, var(--text));
  border: 1px solid color-mix(in srgb, var(--primary) 18%, var(--border));
}
.a2-avatar.size-xs { width: 24px; height: 24px; font-size: 10px; }
.a2-avatar.size-sm { width: 32px; height: 32px; font-size: 12px; }
.a2-avatar.size-md { width: 40px; height: 40px; font-size: 14px; }
.a2-avatar.size-lg { width: 56px; height: 56px; font-size: 18px; }
.a2-avatar img { width: 100%; height: 100%; object-fit: cover; }

.a2-fileupload {
  display: block; padding: 32px; text-align: center; cursor: pointer;
  background: var(--surface); border: 2px dashed var(--border);
  border-radius: var(--radius); color: var(--text-muted);
  transition: border-color var(--transition), background-color var(--transition),
              color var(--transition);
}
.a2-fileupload:hover, .a2-fileupload.over {
  border-color: var(--primary);
  color: color-mix(in srgb, var(--primary) 70%, var(--text-muted));
  background: color-mix(in srgb, var(--primary) 4%, var(--surface));
}
.a2-fileupload input[type="file"] { display: none; }

.a2-notification {
  display: flex; align-items: center; gap: 12px; padding: 12px 16px;
  border-radius: var(--radius-sm); border: 1px solid;
  font-size: var(--fs-md); font-weight: 500;
}
.a2-notification.variant-info    { background: color-mix(in srgb, var(--info) 7%, var(--surface));    border-color: color-mix(in srgb, var(--info) 30%, transparent); color: color-mix(in srgb, var(--info) 80%, var(--text)); }
.a2-notification.variant-success { background: color-mix(in srgb, var(--success) 7%, var(--surface)); border-color: color-mix(in srgb, var(--success) 30%, transparent); color: color-mix(in srgb, var(--success) 80%, var(--text)); }
.a2-notification.variant-warning { background: color-mix(in srgb, var(--warning) 7%, var(--surface)); border-color: color-mix(in srgb, var(--warning) 30%, transparent); color: color-mix(in srgb, var(--warning) 80%, var(--text)); }
.a2-notification.variant-error   { background: color-mix(in srgb, var(--error) 7%, var(--surface));   border-color: color-mix(in srgb, var(--error) 30%, transparent); color: color-mix(in srgb, var(--error) 80%, var(--text)); }
.a2-notification-close {
  margin-left: auto; background: transparent; border: none;
  font-size: 20px; line-height: 1; cursor: pointer; color: inherit;
  opacity: 0.7; transition: opacity var(--transition);
}
.a2-notification-close:hover { opacity: 1; }

/* ========== Load errors detail block ========== */

.a2-load-errors {
  margin-top: 24px; padding: 12px 14px;
  border: 1px solid color-mix(in srgb, var(--warning) 35%, transparent);
  background: color-mix(in srgb, var(--warning) 7%, var(--surface));
  border-radius: var(--radius-sm); font-size: var(--fs-sm);
}
.a2-load-errors summary {
  cursor: pointer; font-weight: 500;
  color: color-mix(in srgb, var(--warning) 80%, var(--text));
}
.a2-load-errors ul { margin: 8px 0 0 0; padding-left: 20px; }

/* ========== Responsive ========== */

@media (max-width: 720px) {
  .a2-row { flex-direction: column; }
  .a2-row > .a2-card { flex: 1; }
  .a2-root { padding: 20px 16px 48px; }
  .a2-text.use-heading { font-size: 22px; }
  .a2-cell-trunc { max-width: 160px; }
}
`;
